import "./App.css";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "./scrolltotop";
import LoadingState from "./Components/LoadingState";
import Header from "./Components/Header";
import Footer from "./Components/Footer";
import RouteComponent from "./Router/RouteComponent";
import { RoutesPaths } from "./Router/constant";
import {
  verifyToken,
  readMe,
  logoutAction,
  AUTH_TOKEN_KEY,
} from "./actions/auth";
import { PAUSED_EVENT, SIGNED_OUT_EVENT } from "./services/axios";

// Reachable without a session; everything else needs one.
const PUBLIC_ROUTES = [
  RoutesPaths.LOGIN,
  RoutesPaths.SIGNUP,
  RoutesPaths.VERIFY_OTP,
  RoutesPaths.FORGOT_PASSWORD,
  // Checkout creates the account it then charges, so it has to be reachable
  // before there is a session to check. It is deliberately not an onboarding
  // route: a signed-in customer coming here to upgrade should stay, not be
  // sent to the dashboard for already having an assistant.
  RoutesPaths.CHECKOUT,
];

// Sign-up and onboarding. Landing on one of these with an assistant already
// built means the steps are done, so /auth/me sends the user to the dashboard.
// Password reset is left out — that flow has to finish on its own, and so is
// /create-assistant: going there with an assistant already is the point of it.
const ONBOARDING_ROUTES = [
  RoutesPaths.LOGIN,
  RoutesPaths.SIGNUP,
  RoutesPaths.VERIFY_OTP,
];

// The pages that carry the marketing header and footer rather than the app
// shell. Sign-in and the steps were one route until they were split, and both
// still look the same way.
const SHELL_FREE_ROUTES = [RoutesPaths.LOGIN, RoutesPaths.CREATE_ASSISTANT];

// Where a paused account is left alone. Billing is where the way out is, and
// Install & Settings is where assistants are deleted - which a paused account
// is still allowed to do, and has to be able to do to fit a smaller plan.
const PAUSED_ROUTES = [RoutesPaths.BILLING, RoutesPaths.INSTALL_SETTINGS];

// Some paths carry a trailing slash in the route table and some don't.
const normalize = (path) =>
  path.length > 1 ? path.replace(/\/+$/, "") : path;

const isPublicRoute = (path) =>
  PUBLIC_ROUTES.some((route) => normalize(route) === normalize(path));

const isOnboardingRoute = (path) =>
  ONBOARDING_ROUTES.some((route) => normalize(route) === normalize(path));

// The public checkout sends someone here after paying: that flow creates an
// account without a session, by design, so the sign-in form says why it is
// being shown rather than appearing for no reason. ?status=success is kept as
// well, for any link still carrying it.
const paidReturnState = () =>
  new URLSearchParams(window.location.search).get("status") === "success"
    ? { paid: true }
    : undefined;

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAppShellRoute = !SHELL_FREE_ROUTES.some(
    (route) => normalize(route) === normalize(location.pathname),
  );

  // The path as it was on load, so the check below reflects where the refresh
  // landed rather than wherever the user navigates afterwards.
  const entryPath = useRef(location.pathname).current;
  // react-router rebuilds `navigate` on every location change, which would
  // otherwise re-run this effect — and re-redirect — on each navigation.
  const bootstrapped = useRef(false);
  const [checkingSession, setCheckingSession] = useState(true);
  // Covers the moment between a request coming back signed out and the sign-in
  // form appearing. Without it the page the user is on stays up, with each of
  // its panels reporting "Not signed in" in turn, and then jumps - which reads
  // as a fault rather than as a session that ended.
  const [signingOut, setSigningOut] = useState(false);

  // A refresh wipes redux, so the session is re-established from GET /auth/me
  // before any route renders. Lives here rather than in Header: Header only
  // mounts on the login route, so the check has to sit above the routes.
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    const bootstrap = async () => {
      // No stored token means there's nothing for /auth/me to validate.
      if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
        setCheckingSession(false);
        if (!isPublicRoute(entryPath)) {
          navigate(RoutesPaths.LOGIN, { replace: true });
        }
        return;
      }

      const resp = await dispatch(verifyToken());

      // Only a failed session redirects; a valid one leaves the user where
      // they are. Judged on the record rather than the HTTP code: a signed-out
      // /auth/me can answer 200 with { status: false, error: "Not signed in" }.
      if (!readMe(resp)) {
        setCheckingSession(false);

        // verifyToken() already cleared the dead session.
        navigate(RoutesPaths.LOGIN, {
          replace: true,
          state: paidReturnState(),
        });
        return;
      }

      // Landing on an auth route with a live session means there is nothing to
      // sign in to. Send them wherever they actually belong: the dashboard once
      // an assistant exists, otherwise back into the onboarding steps. Without
      // the second branch a refresh mid-onboarding dropped the user on the
      // sign-in form and asked them to authenticate all over again.
      if (isOnboardingRoute(entryPath)) {
        if (readMe(resp)?.hasAssistant) {
          navigate(RoutesPaths.DASHBOARD, { replace: true });
        } else {
          navigate(RoutesPaths.CREATE_ASSISTANT, { replace: true });
        }
      }

      setCheckingSession(false);
    };

    // No cancel flag here on purpose: StrictMode's remount would trip it while
    // the once-guard blocks the retry, leaving the spinner up forever.
    bootstrap();
  }, [dispatch, navigate, entryPath]);

  // Whichever request discovers that the session is gone, the answer is the
  // same: stop, clear what is left of it, and ask for a sign-in. Without this
  // the page stays up with every panel on it reporting "Not signed in", and
  // only a refresh - which runs the boot check above - moves anyone on.
  useEffect(() => {
    const onSignedOut = () => {
      // The public pages call the API without a session on purpose: the
      // checkout page quotes a plan before the account exists, and the sign-in
      // form is where this would send them anyway.
      if (isPublicRoute(location.pathname)) return;

      // Up before anything else, so the page underneath stops being read as
      // the thing that is broken.
      setSigningOut(true);
      dispatch(logoutAction());
      navigate(RoutesPaths.LOGIN, { replace: true, state: paidReturnState() });
    };

    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut);
    return () => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut);
  }, [dispatch, navigate, location.pathname]);

  useEffect(() => {
    if (signingOut && isPublicRoute(location.pathname)) setSigningOut(false);
  }, [signingOut, location.pathname]);

  // A paused account answers 402 on every owner API, so whichever page the
  // owner is on would fill with errors it cannot do anything about. The
  // request layer announces it once and the app moves them to Billing, which
  // keeps working while paused and carries the way out of it.
  useEffect(() => {
    const onPaused = () => {
      // Already on a page that works while paused: nothing to do.
      const here = normalize(location.pathname);
      if (PAUSED_ROUTES.some((route) => normalize(route) === here)) return;

      navigate(RoutesPaths.BILLING, { replace: true });
    };

    window.addEventListener(PAUSED_EVENT, onPaused);
    return () => window.removeEventListener(PAUSED_EVENT, onPaused);
  }, [navigate, location.pathname]);

  // Rendering the routes mid-check flashes the dashboard before the redirect.
  if (checkingSession || signingOut) {
    return (
      <div className="app-boot">
        <LoadingState
          title={signingOut ? "Taking you to sign in" : "Loading Hostor"}
          detail={
            signingOut
              ? "Your session has ended."
              : "Checking that you're still signed in."
          }
        />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      {!isAppShellRoute && <Header />}
      <RouteComponent></RouteComponent>
      {!isAppShellRoute && <Footer />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
